import type { Node as PMNode } from "@tiptap/pm/model";
import { AllSelection, NodeSelection, type Selection } from "@tiptap/pm/state";
import type { NodeViewProps } from "@tiptap/react";
import { useCallback, useEffect, useState } from "react";

interface UseNodeIsActiveOptions {
  editor: NodeViewProps["editor"];
  getPos: NodeViewProps["getPos"];
}

const TOP_LEVEL_FIELD_NODE_TYPES = new Set([
  "sectionTitle",
  "paragraphSection",
  "richSection",
  "atomicField",
]);

function isTopLevelFieldNodeType(typeName: string): boolean {
  return TOP_LEVEL_FIELD_NODE_TYPES.has(typeName);
}

function resolveNodePos(getPos: NodeViewProps["getPos"]): number | null {
  try {
    const pos = typeof getPos === "function" ? getPos() : getPos;
    return typeof pos === "number" ? pos : null;
  } catch {
    return null;
  }
}

export function resolveTopLevelFieldStartPos(doc: PMNode, pos: number): number | null {
  const safePos = Math.max(0, Math.min(pos, doc.content.size));
  const $pos = doc.resolve(safePos);
  if ($pos.depth < 1) return null;

  const topLevelNode = $pos.node(1);
  if (!isTopLevelFieldNodeType(topLevelNode.type.name)) return null;

  return $pos.before(1);
}

export function resolveActiveFieldStartPos(
  doc: PMNode,
  selection: Selection,
  isFocused: boolean
): number | null {
  if (!isFocused) return null;
  if (selection instanceof AllSelection) return null;

  if (selection.empty) {
    return resolveTopLevelFieldStartPos(doc, selection.from);
  }

  if (selection instanceof NodeSelection && isTopLevelFieldNodeType(selection.node.type.name)) {
    return null;
  }

  const startFieldPos = resolveTopLevelFieldStartPos(doc, selection.from);
  const endFieldPos = resolveTopLevelFieldStartPos(doc, Math.max(selection.from, selection.to - 1));
  if (startFieldPos == null || endFieldPos == null) return null;

  return startFieldPos === endFieldPos ? startFieldPos : null;
}

export function useNodeIsActive({ editor, getPos }: UseNodeIsActiveOptions): boolean {
  const computeIsActive = useCallback(() => {
    const position = resolveNodePos(getPos);
    if (position == null) return false;

    const activeFieldPos = resolveActiveFieldStartPos(
      editor.state.doc,
      editor.state.selection,
      editor.isFocused
    );
    return activeFieldPos != null && position === activeFieldPos;
  }, [editor, getPos]);

  const [isActive, setIsActive] = useState<boolean>(() => computeIsActive());

  useEffect(() => {
    const sync = () => setIsActive(computeIsActive());

    sync();
    editor.on("selectionUpdate", sync);
    editor.on("focus", sync);
    editor.on("blur", sync);

    return () => {
      editor.off("selectionUpdate", sync);
      editor.off("focus", sync);
      editor.off("blur", sync);
    };
  }, [computeIsActive, editor]);

  return isActive;
}

function documentHasOwnerOverrides(editor: NodeViewProps["editor"]): boolean {
  let hasOverrides = false;

  editor.state.doc.forEach((node) => {
    if (hasOverrides) return;
    const ownerId = node.attrs?.ownerId;
    if (typeof ownerId === "string" && ownerId.trim().length > 0) {
      hasOverrides = true;
    }
  });

  return hasOverrides;
}

export function useDocumentHasOwnerOverrides(editor: NodeViewProps["editor"]): boolean {
  const computeHasOverrides = useCallback(() => documentHasOwnerOverrides(editor), [editor]);
  const [hasOwnerOverrides, setHasOwnerOverrides] = useState<boolean>(() => computeHasOverrides());

  useEffect(() => {
    const sync = () => setHasOwnerOverrides(computeHasOverrides());

    sync();
    editor.on("transaction", sync);

    return () => {
      editor.off("transaction", sync);
    };
  }, [computeHasOverrides, editor]);

  return hasOwnerOverrides;
}
